import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function MicrosoftSharePointDialog({ connector, onClose, onSuccess }) {
  const [site, setSite] = useState('');
  const [libraries, setLibraries] = useState([]);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let active = true;
    setBusy(true);
    const request = connector
      ? api.get(`/sharepoint-microsoft/${connector.id}/libraries`)
      : api.get('/sharepoint-microsoft/status');
    request.then(({ data }) => {
      if (!active) return;
      if (connector) setLibraries(data.libraries);
      else { setEnabled(data.enabled); if (!data.enabled) setError(data.message); }
    }).catch(e => { if (active) setError(message(e)); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [connector]);
  function message(e) {
    const detail = e.response?.data?.detail;
    return typeof detail === 'string' ? detail : detail?.message || 'Unable to connect. Please try again.';
  }
  async function connect() {
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/sharepoint-microsoft/start', { site_url: site.trim() });
      window.location.assign(data.authorization_url);
    } catch (e) { setError(message(e)); setBusy(false); }
  }
  async function save() {
    setBusy(true); setError('');
    try {
      await api.put(`/sharepoint-microsoft/${connector.id}/library`, { drive_id: selected });
      onSuccess(); onClose();
    } catch (e) { setError(message(e)); } finally { setBusy(false); }
  }
  async function disconnect() {
    setBusy(true); setError('');
    try {
      await api.delete(`/connectors/${connector.id}`);
      onSuccess(); onClose();
    } catch (e) { setError(message(e)); } finally { setBusy(false); }
  }
  return <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{connector ? 'Choose a SharePoint library' : 'Connect with Microsoft'}</DialogTitle>
        <DialogDescription>
          Connect using the Microsoft work account that can open your SharePoint site.
          Only the Eva administrator who connects it can browse and import with this connection.
        </DialogDescription>
      </DialogHeader>
      <p className="text-sm">Microsoft permission allows Eva to read SharePoint sites you can access. Eva imports only files you select, as drafts. Copies use Eva access; source permissions are not mirrored.</p>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {connector ? <>
        <label className="text-sm">Document library
          <select className="block w-full border rounded p-2 bg-background" value={selected} disabled={busy} onChange={e => setSelected(e.target.value)}>
            <option value="">Choose a library</option>
            {libraries.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <Button disabled={busy || !selected} onClick={save}>Use this library</Button>
        <p className="text-sm text-muted-foreground">To connect with another account or recover an expired connection, disconnect and connect again. Imported Eva copies remain unchanged. Disconnect removes Eva's saved Microsoft tokens; Microsoft app consent can be managed separately in your Microsoft account.</p>
        <Button variant="outline" disabled={busy} onClick={disconnect}>Disconnect Microsoft account</Button>
      </> : <>
        <label className="text-sm" htmlFor="microsoft-sharepoint-site">SharePoint site link</label>
        <Input id="microsoft-sharepoint-site" autoComplete="off" value={site} onChange={e => setSite(e.target.value)} placeholder="https://yourorganization.sharepoint.com/sites/HR" />
        <p className="text-sm text-muted-foreground">Paste the site's home link, not a document link. If Microsoft requests IT approval, ask your Microsoft administrator to approve Eva's SharePoint read access, then return here. You do not need application IDs or client secrets.</p>
        <Button disabled={busy || !enabled || !site.trim()} onClick={connect}>{busy ? 'Connecting...' : 'Continue to Microsoft'}</Button>
      </>}
    </DialogContent>
  </Dialog>;
}
